import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import { MapPin, Users, TrendingUp, Download, Layers, BarChart3, Target } from 'lucide-react';
import L, { addMapTilerLayer } from '../../utils/leafletSetup';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.heat';

const LocationAnalysis = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [map, setMap] = useState(null);
  const [viewMode, setViewMode] = useState('markers'); // 'markers', 'clusters', 'heatmap'
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    minOrders: 1
  });
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalLocations: 0,
    topCity: '',
    topCounty: '',
    avgOrdersPerLocation: 0
  });
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const clusterLayerRef = useRef(null);
  const heatLayerRef = useRef(null);

  useEffect(() => {
    fetchLocationData();
  }, [filters]);

  // Leaflet initializes synchronously once the container is mounted — no
  // async script loading, no "waiting for API" state needed.
  const setMapRefCallback = (node) => {
    if (node && !mapRef.current) {
      mapRef.current = node;
      initializeMap();
    }
  };

  useEffect(() => {
    // Render map when it's ready, regardless of location data
    if (map) {
      renderMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, locations, viewMode]);

  const initializeMap = () => {
    if (!mapRef.current || map) return;

    const newMap = L.map(mapRef.current).setView([-1.2921, 36.8219], 8);
    addMapTilerLayer(newMap);

    setMap(newMap);
    setLoading(false);
  };

  const fetchLocationData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.error('❌ No admin token found');
        setLoading(false);
        return;
      }

      const baseUrl = API_BASE_URL;
      const url = `${baseUrl}/api/admin/orders/location-analysis?status=${filters.status}&dateRange=${filters.dateRange}&minOrders=${filters.minOrders}`;
      
      console.log('📡 Fetching location data from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Location data received:', data);
        console.log('📊 Location stats:', {
          locationsCount: data.locations?.length || 0,
          totalCustomers: data.stats?.totalCustomers || 0,
          totalLocations: data.stats?.totalLocations || 0
        });
        setLocations(data.locations || []);
        setStats(data.stats || {
          totalCustomers: 0,
          totalLocations: 0,
          topCity: '',
          topCounty: '',
          avgOrdersPerLocation: 0
        });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Failed to fetch location data:', response.status, errorData);
        setLocations([]);
        setStats({
          totalCustomers: 0,
          totalLocations: 0,
          topCity: '',
          topCounty: '',
          avgOrdersPerLocation: 0
        });
      }
    } catch (error) {
      console.error('❌ Error fetching location data:', error);
      setLocations([]);
      setStats({
        totalCustomers: 0,
        totalLocations: 0,
        topCity: '',
        topCounty: '',
        avgOrdersPerLocation: 0
      });
    }
  };

  const clearLayers = () => {
    if (markersLayerRef.current) { map.removeLayer(markersLayerRef.current); markersLayerRef.current = null; }
    if (clusterLayerRef.current) { map.removeLayer(clusterLayerRef.current); clusterLayerRef.current = null; }
    if (heatLayerRef.current) { map.removeLayer(heatLayerRef.current); heatLayerRef.current = null; }
  };

  const renderMap = () => {
    if (!map) return;

    clearLayers();

    const validLocations = locations.filter(loc => loc.lat && loc.lng && !isNaN(loc.lat) && !isNaN(loc.lng));

    if (validLocations.length === 0) {
      map.setView([-1.2921, 36.8219], 8);
      return;
    }

    map.fitBounds(L.latLngBounds(validLocations.map(loc => [loc.lat, loc.lng])), { padding: [40, 40] });

    if (viewMode === 'heatmap') {
      renderHeatmap(validLocations);
    } else if (viewMode === 'clusters') {
      renderClusters(validLocations);
    } else {
      renderMarkers(validLocations);
    }
  };

  const popupHtml = (location) => {
    const revenue = location.totalRevenue ? `KSh ${location.totalRevenue.toLocaleString()}` : 'N/A';
    const recentOrdersList = location.recentOrders && location.recentOrders.length > 0
      ? location.recentOrders.map(order =>
          `<li style="margin: 2px 0; font-size: 12px; color: #555;">
            ${order.orderNumber} - ${order.status} (KSh ${(order.total || 0).toLocaleString()})
          </li>`
        ).join('')
      : '<li style="color: #999; font-size: 12px;">No recent orders</li>';

    return `
      <div style="padding: 4px; min-width: 260px; max-width: 320px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <h3 style="margin: 0 0 10px 0; font-weight: 600; color: #1a1a1a; font-size: 15px; border-bottom: 2px solid #d44100; padding-bottom: 6px;">
          📍 ${location.address || 'Customer Location'}
        </h3>
        <div style="margin: 8px 0;">
          <p style="margin: 4px 0; color: #333; font-size: 13px;">
            <strong style="color: #d44100;">📍 Location:</strong><br>
            ${location.city ? `${location.city}, ` : ''}${location.county || ''}${location.country ? `, ${location.country}` : ''}
          </p>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 10px 0; padding: 10px; background: #f5f5f5; border-radius: 6px;">
          <div>
            <div style="font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 0.5px;">Customers</div>
            <div style="font-size: 18px; font-weight: 700; color: #d44100;">${location.customerCount}</div>
          </div>
          <div>
            <div style="font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 0.5px;">Orders</div>
            <div style="font-size: 18px; font-weight: 700; color: #0f4c5c;">${location.orderCount}</div>
          </div>
          <div style="grid-column: 1 / -1;">
            <div style="font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 0.5px;">Total Revenue</div>
            <div style="font-size: 16px; font-weight: 700; color: #22c55e;">${revenue}</div>
          </div>
        </div>
        ${location.recentOrders && location.recentOrders.length > 0 ? `
          <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e5e5;">
            <div style="font-size: 12px; font-weight: 600; color: #333; margin-bottom: 6px;">Recent Orders:</div>
            <ul style="margin: 0; padding-left: 18px; max-height: 120px; overflow-y: auto;">
              ${recentOrdersList}
            </ul>
          </div>
        ` : ''}
        <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #e5e5e5; font-size: 11px; color: #999;">
          Coordinates: ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}
        </div>
      </div>
    `;
  };

  const renderMarkers = (validLocations) => {
    const layer = L.layerGroup();
    validLocations.forEach((location) => {
      const baseSize = 8, maxSize = 24;
      const sizeMultiplier = Math.min(location.customerCount / 5, 3);
      const radius = Math.max(baseSize, Math.min(maxSize, baseSize * sizeMultiplier));
      const color = getColorForCustomerCount(location.customerCount);

      L.circleMarker([location.lat, location.lng], {
        radius,
        fillColor: color,
        color: '#ffffff',
        weight: 2,
        fillOpacity: 0.8,
      })
        .bindPopup(popupHtml(location))
        .addTo(layer);
    });
    layer.addTo(map);
    markersLayerRef.current = layer;
  };

  const renderClusters = (validLocations) => {
    const cluster = L.markerClusterGroup({
      iconCreateFunction: (c) => L.divIcon({
        html: `<div style="width:40px;height:40px;border-radius:50%;background:#d44100;opacity:0.85;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:13px;border:2px solid #fff;">${c.getChildCount()}</div>`,
        className: '',
        iconSize: [40, 40],
      }),
    });

    validLocations.forEach((location) => {
      const color = getColorForCustomerCount(location.customerCount);
      const radius = Math.max(6, Math.min(14, location.customerCount * 1.2));
      const marker = L.circleMarker([location.lat, location.lng], {
        radius,
        fillColor: color,
        color: '#ffffff',
        weight: 2,
        fillOpacity: 0.8,
      }).bindPopup(popupHtml(location));
      cluster.addLayer(marker);
    });

    cluster.addTo(map);
    clusterLayerRef.current = cluster;
  };

  const renderHeatmap = (validLocations) => {
    const points = validLocations.map((location) => [
      location.lat,
      location.lng,
      Math.min(location.customerCount || location.orderCount || 1, 20) / 20, // normalize 0-1
    ]);

    const heat = L.heatLayer(points, {
      radius: 35,
      blur: 25,
      maxZoom: 14,
      gradient: { 0.2: '#00ffff', 0.4: '#0080ff', 0.6: '#8000ff', 0.8: '#ff0040', 1.0: '#ff0000' },
    });

    heat.addTo(map);
    heatLayerRef.current = heat;
  };

  const getColorForOrderCount = (count) => {
    if (count >= 50) return '#dc2626'; // Red - high density
    if (count >= 20) return '#f59e0b'; // Orange - medium-high
    if (count >= 10) return '#eab308'; // Yellow - medium
    if (count >= 5) return '#22c55e'; // Green - medium-low
    return '#3b82f6'; // Blue - low
  };

  const getColorForCustomerCount = (count) => {
    // Color based on customer density (more customers = warmer color)
    if (count >= 20) return '#dc2626'; // Red - very high customer density
    if (count >= 10) return '#f59e0b'; // Orange - high customer density
    if (count >= 5) return '#eab308'; // Yellow - medium customer density
    if (count >= 3) return '#22c55e'; // Green - medium-low customer density
    return '#3b82f6'; // Blue - low customer density
  };

  const exportData = () => {
    const csv = [
      ['Address', 'City', 'County', 'Country', 'Orders', 'Customers', 'Latitude', 'Longitude'],
      ...locations.map(loc => [
        loc.address || '',
        loc.city || '',
        loc.county || '',
        loc.country || '',
        loc.orderCount || 0,
        loc.customerCount || 0,
        loc.lat || '',
        loc.lng || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `location-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <MapPin className="h-6 w-6 mr-2 text-[#d44100]" />
            Location Analysis
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Analyze customer distribution to optimize physical store locations
          </p>
        </div>
        <button
          onClick={exportData}
          className="flex items-center px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] transition-colors"
        >
          <Download className="h-4 w-4 mr-2" />
          Export Data
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-gray-600">Total Customers</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalCustomers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <MapPin className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-gray-600">Unique Locations</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalLocations}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Target className="h-5 w-5 text-orange-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-gray-600">Top City</p>
              <p className="text-xl font-bold text-gray-900">{stats.topCity || 'N/A'}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-gray-600">Avg Orders/Location</p>
              <p className="text-xl font-bold text-gray-900">{stats.avgOrdersPerLocation.toFixed(1)}</p>
            </div>
          </div>
        </div>
        {stats.totalRevenue && (
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-xl font-bold text-gray-900">KSh {stats.totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters and View Controls */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Order Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
            >
              <option value="all">All Statuses</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="DELIVERED">Delivered</option>
              <option value="PROCESSING">Processing</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
            >
              <option value="all">All Time</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Last Year</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Min Orders</label>
            <input
              type="number"
              min="1"
              value={filters.minOrders}
              onChange={(e) => setFilters({ ...filters, minOrders: parseInt(e.target.value) || 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">View Mode</label>
            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('markers')}
                className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                  viewMode === 'markers' ? 'bg-[#d44100] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <MapPin className="h-4 w-4 inline mr-1" />
                Markers
              </button>
              <button
                onClick={() => setViewMode('clusters')}
                className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                  viewMode === 'clusters' ? 'bg-[#d44100] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Layers className="h-4 w-4 inline mr-1" />
                Clusters
              </button>
              <button
                onClick={() => setViewMode('heatmap')}
                className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                  viewMode === 'heatmap' ? 'bg-[#d44100] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <BarChart3 className="h-4 w-4 inline mr-1" />
                Heatmap
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden relative">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d44100] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading location data...</p>
            </div>
          </div>
        ) : (
          <>
            <div ref={setMapRefCallback} style={{ width: '100%', height: '600px', minHeight: '600px', position: 'relative' }} />
            {locations.length === 0 && map && !loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-90 z-10 pointer-events-none">
                <div className="text-center bg-white p-4 rounded-lg shadow">
                  <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-semibold">No location data available</p>
                  <p className="text-sm text-gray-500 mt-2">Try adjusting your filters or check if orders have shipping addresses</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 bg-white p-4 rounded-lg shadow">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Legend</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-blue-500 mr-2"></div>
            <span className="text-sm text-gray-600">1-2 customers</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
            <span className="text-sm text-gray-600">3-4 customers</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-yellow-500 mr-2"></div>
            <span className="text-sm text-gray-600">5-9 customers</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-orange-500 mr-2"></div>
            <span className="text-sm text-gray-600">10-19 customers</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
            <span className="text-sm text-gray-600">20+ customers</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationAnalysis;

